import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Phone, PhoneOutgoing, PhoneIncoming } from "@phosphor-icons/react";

export default function AddCallPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [notes, setNotes] = useState("");
  const [duration, setDuration] = useState("");
  const [callType, setCallType] = useState("outbound");

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      setCustomer(response.data);
    } catch (error) {
      toast.error("Customer not found");
      navigate("/customers");
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!notes.trim()) {
      toast.error("Please add notes about the call");
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/calls`, {
        customer_id: id,
        notes: notes,
        duration_minutes: duration ? parseInt(duration) : null,
        call_type: callType,
      });

      toast.success("Call logged successfully");
      navigate(`/customers/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log call");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="add-call-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="add-call-page">
      {/* Header */}
      <div className="top-bar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/customers/${id}`)}
            className="p-2 -ml-2 text-[#4B5563] hover:text-[#0A0A0A]"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="overline">LOG CALL</p>
            <h1 className="top-bar-title">{customer?.name}</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Call Type */}
        <div className="card-flat p-4">
          <Label className="text-[#0A0A0A] font-medium mb-3 block">
            Call Type
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setCallType("outbound")}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors ${
                callType === "outbound"
                  ? "border-[#0055FF] bg-[#F0F5FF]"
                  : "border-[#E5E7EB] bg-white"
              }`}
              data-testid="call-type-outbound"
            >
              <PhoneOutgoing 
                size={28} 
                className={callType === "outbound" ? "text-[#0055FF]" : "text-[#9CA3AF]"} 
                weight="fill" 
              />
              <span className={`font-medium ${callType === "outbound" ? "text-[#0055FF]" : "text-[#4B5563]"}`}>
                Outbound
              </span>
              <span className="text-xs text-[#9CA3AF]">I called them</span>
            </button>
            <button
              type="button"
              onClick={() => setCallType("inbound")}
              className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors ${
                callType === "inbound"
                  ? "border-[#10B981] bg-[#D1FAE5]"
                  : "border-[#E5E7EB] bg-white"
              }`}
              data-testid="call-type-inbound"
            >
              <PhoneIncoming 
                size={28} 
                className={callType === "inbound" ? "text-[#10B981]" : "text-[#9CA3AF]"} 
                weight="fill" 
              />
              <span className={`font-medium ${callType === "inbound" ? "text-[#10B981]" : "text-[#4B5563]"}`}>
                Inbound
              </span>
              <span className="text-xs text-[#9CA3AF]">They called me</span>
            </button>
          </div>
        </div>

        {/* Duration */}
        <div className="card-flat p-4">
          <Label htmlFor="duration" className="text-[#0A0A0A] font-medium">
            Call Duration (minutes) - Optional
          </Label>
          <Input
            id="duration"
            type="number"
            min="1"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g., 15"
            className="mt-1.5 h-12 border-[#E5E7EB]"
            data-testid="call-duration-input"
          />
        </div>

        {/* Notes */}
        <div className="card-flat p-4">
          <Label htmlFor="notes" className="text-[#0A0A0A] font-medium">
            Call Notes *
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What was discussed? Any follow-up needed?"
            rows={5}
            className="mt-1.5 border-[#E5E7EB] resize-none"
            data-testid="call-notes-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading || !notes.trim()}
          className="w-full h-12 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full disabled:opacity-50"
          data-testid="submit-call-button"
        >
          {loading ? "Logging..." : "Log Call"}
        </Button>
      </form>
    </div>
  );
}
